import { DEFAULT_MARKET_AUCTION_DURATION_MS } from '@velocity-exchange/common';
import {
	CandleTypeByMarketIdKey,
	EventSubscriberType,
	GlobalEventSubscriberType,
	UserSettings,
} from './EnvironmentVariableTypes';

// --- User Settings

/** Wall-clock default ramp, kept in step with the dlob server's own default. */
export const DEFAULT_MARKET_AUCTION_DURATION_SECONDS =
	DEFAULT_MARKET_AUCTION_DURATION_MS / 1000;
const DEFAULT_AUCTION_END_PRICE_OFFSET = 0.1;
const DEFAULT_MAX_PRIORITY_FEE_SOL_CAP = 0.001;
const DEFAULT_AUCTION_END_PRICE_FROM = 'worst';
const DEFAULT_CANDLE_TYPE_BY_MARKET_INDEX: CandleTypeByMarketIdKey = {};

const AGGRO_TRADER_PROFILE: Partial<UserSettings> = {
	slippageTolerance: 'dynamic',
	auctionDurationSeconds: DEFAULT_MARKET_AUCTION_DURATION_SECONDS,
	auctionStartPriceOffset: 'marketBased',
	auctionEndPriceOffset: DEFAULT_AUCTION_END_PRICE_OFFSET,
	auctionEndPriceOffsetFrom: DEFAULT_AUCTION_END_PRICE_FROM,
	auctionStartPriceOffsetFrom: 'marketBased',
	oracleOffsetOrdersEnabled: true,
	placeAndTakeEnabled: true,
	traderProfile: 'default',
};

//@ts-ignore
const defaultTradeSettings: Pick<
	UserSettings,
	| 'slippageTolerance'
	| 'priorityFee'
	| 'customPriorityFee'
	| 'maxPriorityFeeCap'
	| 'candleTypeByMarketIdKey'
	| 'auctionDurationSeconds'
	| 'auctionStartPriceOffset'
	| 'auctionEndPriceOffset'
	| 'auctionStartPriceOffsetFrom'
	| 'auctionEndPriceOffsetFrom'
	| 'oracleOffsetOrdersEnabled'
	| 'customMarginRatio'
	| 'accountSubscriberType'
	| 'placeAndTakeEnabled'
	| 'traderProfile'
	| 'swiftEnabled'
> = {
	priorityFee: 'dynamic',
	customPriorityFee: undefined as unknown as number,
	maxPriorityFeeCap: DEFAULT_MAX_PRIORITY_FEE_SOL_CAP,
	candleTypeByMarketIdKey: DEFAULT_CANDLE_TYPE_BY_MARKET_INDEX,
	customMarginRatio: undefined as unknown as number,
	accountSubscriberType: 'polling',
	...AGGRO_TRADER_PROFILE,
	// so we can tell if its a user's first time using trading profiles or not
	traderProfile: undefined,
	swiftEnabled: undefined,
};

export const defaultUserSettings: UserSettings = {
	// Preview/dev deployments boot into devnet. This has to be the default rather
	// than a fallback applied while resolving settings, because SettingsProvider
	// writes these defaults to localStorage verbatim on a first visit — a `false`
	// here gets persisted and silently pins later loads to mainnet.
	devnetOverride: process.env.NEXT_PUBLIC_DEFAULT_TO_DEVNET === 'true',
	stagingDlobServerOverride: false,
	stagingDataServerOverride: false,
	enableVersionedTx: true,
	showPnlOnClose: true,
	showTradeConfirmation: false,
	showClosePositionConfirmation: true,
	showAccountValues: true,
	showFillNotifications: true,
	showZeroValues: true,
	blinkOnValueChange: true,
	showPositionsAndOrdersOnChart: true,
	showLiquidationPriceOnChart: true,
	showMarketBar: true,
	showElfaWidget: true,
	showBuysAndSellsOnChart: true,
	perpPageTableSizePreference: undefined as unknown as string,
	explorer: 'solana',
	ignoreToasts: {
		walletVersionedTxCompatibility: false,
	},
	// </ ignore toast options >
	cancelExistingOrdersOnClosePosition: true,
	rpcEndpoint: undefined as unknown as { label: string; value: string },
	directRouteSwaps: false,
	version: undefined,
	theme: null,
	globalEventsSubscriptionType:
		undefined as unknown as GlobalEventSubscriberType,
	selfEventsSubscriptionType: undefined as unknown as EventSubscriberType,
	showOrderbook: true,
	showOrderbookOnMobileTradeView: true,
	swapClientType: 'titan', // Default to Titan
	enableSignlessTrading: true,
	...defaultTradeSettings,
};

export const TRADER_PROFILE_OPTIONS = [
	{
		label: 'Default',
		description: 'Immediate fills and shorter auction durations',
		value: AGGRO_TRADER_PROFILE,
		key: 'default',
	},
	{
		label: 'Custom',
		description: 'For advanced traders only',
		value: undefined,
		key: 'custom',
	},
];
